import * as React from "react";
import { createContext, useState, useEffect, useContext } from "react";
import axios from "axios";

export interface HostSystemSupportedFunctionality {
  // updateUserInformation: Patron;
  pinEditable: boolean;
  phoneNumberEditable: boolean;
  addressEditable: boolean;
  emailEditable: boolean;
  canPlaceItemRequests: boolean;
  canPlaceTitleRequests: boolean;
  supportsUserFunctionality: boolean;
  supportsAvailabilityFunctionality: boolean;
  supportsHoldingsCollection: boolean;
  supportsHoldingsCallNumber: boolean;
  supportsBookingsFunctionality: boolean;
  supportsSingleLogon: boolean;
  supportsLoanHistory: boolean;
  useGuardianProhibition: boolean;
  includeGuardiansInAD: boolean;
  allowGuardianToViewDependentActivity: boolean;
}

export const SupportedFunctionalityHolder = (() => {
  let supportedFunctionality: HostSystemSupportedFunctionality;
  const subscribers: ((event: HostSystemSupportedFunctionality) => void)[] = [];

  const fetchData = async () => {
    const { data } = await axios.get<HostSystemSupportedFunctionality>(
      "/hostSystem/supportedFunctionality"
    );

    supportedFunctionality = data;

    subscribers.forEach((sub) => sub({ ...supportedFunctionality }));
    return { ...supportedFunctionality };
  };

  const subscribe = (
    subscriber: (
      supportedFunctionality: HostSystemSupportedFunctionality
    ) => void
  ) => {
    // Add the subscriber function to the list of subscribers
    subscribers.push(subscriber);

    if (supportedFunctionality) {
      subscriber({ ...supportedFunctionality });
    } else {
      fetchData();
    }

    // Return an unsubscribe function
    return () => {
      const index = subscribers.indexOf(subscriber);
      if (index !== -1) {
        subscribers.splice(index, 1); // Remove the subscriber when unsubscribed
      }
    };
  };

  return {
    subscribe,
    fetchData,
    getSupportedFunctionality: async () => {
      if (supportedFunctionality) {
        return supportedFunctionality;
      } else {
        return fetchData();
      }
    },
  };
})();

const SupportedFunctionalityContext = createContext<{
  supportedFunctionality: HostSystemSupportedFunctionality;
}>(null);

export const SupportedFunctionalityProvider = ({
  children,
}: React.PropsWithChildren) => {
  const [supportedFunctionality, setSupportedFunctionality] =
    useState<HostSystemSupportedFunctionality>(null);

  useEffect(() => {
    const unsubscribe = SupportedFunctionalityHolder.subscribe((data) => {
      setSupportedFunctionality(data);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const contextValue = {
    supportedFunctionality,
  };

  return (
    <SupportedFunctionalityContext.Provider value={contextValue}>
      {children}
    </SupportedFunctionalityContext.Provider>
  );
};

export function useSupportedFunctionality() {
  const context = useContext(SupportedFunctionalityContext);

  if (!context) {
    throw new Error(
      "useSupportedFunctionality must be used within a SupportedFunctionalityProvider"
    );
  }

  return context;
}
